import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  CreditCardIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ComputerDesktopIcon,
  LightBulbIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';

interface FilterProps {
  dateRange: {
    start: string;
    end: string;
    preset: string;
  };
  departments: string[];
  metrics: string[];
  detailLevel: string;
  displayType: string;
  currency: string;
}

interface ExpenseAnalysisProps {
  filters: FilterProps;
}

const ExpenseAnalysis: React.FC<ExpenseAnalysisProps> = ({ filters }) => {
  const { t, direction } = useLanguage();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');

  // Expense summary metrics
  const expenseSummary = [
    {
      title: 'إجمالي المصروفات',
      value: '1,923,200',
      change: '+8.3%',
      trend: 'up',
      icon: CreditCardIcon,
      color: 'from-red-500 to-red-600',
      budget: '2,100,000',
      variance: '-8.4%'
    },
    {
      title: 'متوسط المصروفات الشهرية',
      value: '320,533',
      change: '+5.2%',
      trend: 'up',
      icon: BuildingOfficeIcon,
      color: 'from-orange-500 to-orange-600',
      budget: '350,000',
      variance: '-8.4%'
    },
    {
      title: 'المصروفات المتغيرة',
      value: '1,153,920',
      change: '+12.1%',
      trend: 'up',
      icon: ArrowTrendingUpIcon,
      color: 'from-yellow-500 to-yellow-600',
      budget: '1,200,000',
      variance: '-3.8%'
    },
    {
      title: 'المصروفات الثابتة',
      value: '769,280',
      change: '+3.2%',
      trend: 'up',
      icon: CheckCircleIcon,
      color: 'from-blue-500 to-blue-600',
      budget: '900,000',
      variance: '-14.5%'
    }
  ];

  // Monthly expense trend
  const monthlyExpenseData = [
    { month: 'يناير', total: 1800000, fixed: 720000, variable: 1080000, budget: 1950000 },
    { month: 'فبراير', total: 1850000, fixed: 740000, variable: 1110000, budget: 1950000 },
    { month: 'مارس', total: 1900000, fixed: 760000, variable: 1140000, budget: 2000000 },
    { month: 'أبريل', total: 1950000, fixed: 780000, variable: 1170000, budget: 2000000 },
    { month: 'مايو', total: 1920000, fixed: 768000, variable: 1152000, budget: 2050000 },
    { month: 'يونيو', total: 1923200, fixed: 769280, variable: 1153920, budget: 2100000 }
  ];

  // Expense categories
  const expenseCategories = [
    {
      category: 'الرواتب والأجور',
      amount: 865440,
      percentage: 45,
      budget: 945000,
      variance: -8.4,
      color: '#3B82F6',
      icon: UserGroupIcon
    },
    {
      category: 'التسويق والإعلان',
      amount: 384640,
      percentage: 20,
      budget: 420000,
      variance: -8.4,
      color: '#10B981',
      icon: LightBulbIcon
    },
    {
      category: 'المصروفات التشغيلية',
      amount: 288480,
      percentage: 15,
      budget: 315000,
      variance: -8.4,
      color: '#F59E0B',
      icon: BuildingOfficeIcon
    },
    {
      category: 'الإيجارات والمرافق',
      amount: 230784,
      percentage: 12,
      budget: 252000,
      variance: -8.4,
      color: '#EF4444',
      icon: BuildingOfficeIcon
    },
    {
      category: 'التقنية والبرمجيات',
      amount: 153856,
      percentage: 8,
      budget: 168000,
      variance: -8.4,
      color: '#8B5CF6',
      icon: ComputerDesktopIcon
    }
  ];

  // Department expense comparison
  const departmentExpenses = [
    { department: 'التسويق', current: 420000, previous: 380000, budget: 450000, variance: -6.7 },
    { department: 'المبيعات', current: 650000, previous: 620000, budget: 700000, variance: -7.1 },
    { department: 'العمليات', current: 380000, previous: 360000, budget: 400000, variance: -5.0 },
    { department: 'الموارد البشرية', current: 280000, previous: 270000, budget: 300000, variance: -6.7 },
    { department: 'تقنية المعلومات', current: 193200, previous: 180000, budget: 250000, variance: -22.7 }
  ];

  // Optimization opportunities
  const optimizationOpportunities = [
    {
      category: 'التسويق الرقمي',
      currentSpend: 192320,
      potentialSaving: 28848,
      savingPercentage: 15,
      recommendation: 'تحسين استهداف الإعلانات وإعادة توزيع الميزانية',
      impact: 'high'
    },
    {
      category: 'الاشتراكات البرمجية',
      currentSpend: 92314,
      potentialSaving: 18463,
      savingPercentage: 20,
      recommendation: 'مراجعة الاشتراكات غير المستخدمة والتفاوض على الأسعار',
      impact: 'medium'
    },
    {
      category: 'المصروفات التشغيلية',
      currentSpend: 144240,
      potentialSaving: 14424,
      savingPercentage: 10,
      recommendation: 'تحسين عمليات الصيانة الوقائية',
      impact: 'medium'
    }
  ];

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Expense Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {expenseSummary.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                    <div className={`w-10 h-10 bg-gradient-to-r ${metric.color} rounded-lg p-2`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {metric.title}
                  </h3>
                  
                  <div className="flex items-baseline space-x-2 rtl:space-x-reverse mb-2">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {metric.value.toLocaleString()}
                    </span>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      ر.س
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        الميزانية: {metric.budget.toLocaleString()} ر.س
                      </span>
                      <span className={`text-xs font-medium ${
                        metric.variance.startsWith('-') 
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {metric.variance}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                        metric.trend === 'up' 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {metric.trend === 'up' ? (
                          <ArrowTrendingUpIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                        ) : (
                          <ArrowTrendingDownIcon className="w-3 h-3 ml-1 rtl:ml-0 rtl:mr-1" />
                        )}
                        {metric.change}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Expense Trend and Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Expense Trend */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              اتجاه المصروفات الشهرية
            </h3>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="monthly">شهرياً</option>
              <option value="quarterly">ربع سنوي</option>
              <option value="yearly">سنوياً</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={monthlyExpenseData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}م`}
              />
              <Tooltip
                formatter={(value: any, name: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, name]}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Area
                type="monotone"
                dataKey="fixed"
                stackId="1"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.6}
                name="المصروفات الثابتة"
              />
              <Area
                type="monotone"
                dataKey="variable"
                stackId="1"
                stroke="#EF4444"
                fill="#EF4444"
                fillOpacity={0.6}
                name="المصروفات المتغيرة"
              />
              <Line
                type="monotone"
                dataKey="budget"
                stroke="#10B981"
                strokeWidth={3}
                strokeDasharray="5 5"
                name="الميزانية المخططة"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Expense Categories Pie Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            تفصيل المصروفات حسب الفئة
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={expenseCategories}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="amount"
                label={({ category, percentage }) => `${category}: ${percentage}%`}
                labelLine={false}
              >
                {expenseCategories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, 'المبلغ']}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Department Comparison */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          مقارنة مصروفات الأقسام
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={departmentExpenses}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="department" 
              className="text-xs"
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              className="text-xs"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => `${(value / 1000).toLocaleString()}ك`}
            />
            <Tooltip
              formatter={(value: any, name: any) => [`${(value / 1000).toLocaleString()} ألف ر.س`, name]}
              labelStyle={{ color: '#374151' }}
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            />
            <Bar dataKey="current" fill="#EF4444" name="الفترة الحالية" radius={[4, 4, 0, 0]} />
            <Bar dataKey="previous" fill="#F87171" name="الفترة السابقة" radius={[4, 4, 0, 0]} />
            <Bar dataKey="budget" fill="#FCA5A5" name="الميزانية" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Optimization Opportunities */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <LightBulbIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
          فرص التحسين والتوفير
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {optimizationOpportunities.map((opportunity, index) => (
            <motion.div
              key={opportunity.category}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {opportunity.category}
                </h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getImpactColor(opportunity.impact)}`}>
                  {opportunity.impact === 'high' ? 'تأثير عالي' : opportunity.impact === 'medium' ? 'تأثير متوسط' : 'تأثير منخفض'}
                </span>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">الإنفاق الحالي:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {opportunity.currentSpend.toLocaleString()} ر.س
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">التوفير المحتمل:</span>
                  <span className="text-sm font-bold text-green-600 dark:text-green-400">
                    {opportunity.potentialSaving.toLocaleString()} ر.س ({opportunity.savingPercentage}%)
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {opportunity.recommendation}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default ExpenseAnalysis;
